import { Box, Button, FlexBox, IconButton } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { memo, useCallback } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { ElevionLeafNode, ElevionLogicalNode } from 'utils/elevion';

import { ElevionCondition } from '../elevionCondition';
import { ElevionLogicalOp } from '../elevionLogicalOp';

import styles from './elevionConditionLogica.module.scss';

type OptionsProps = {
  /** The name of the parent. */
  parentName?: string;
  index?: number;
};

const emptyLeafNode: ElevionLeafNode = {
  comparator: '==',
  field: '',
  field_type: 'sfield',
  value: '',
};

const emptyLogicalNode: ElevionLogicalNode = {
  logicalOp: 'AND',
  nodes: [emptyLeafNode],
};

/** Represents the field for managing options of a custom select attribute. */
export const ElevionConditionLogical = memo(function Options({
  parentName,
  index,
}: OptionsProps): JSX.Element {
  const { control } = useFormContext();

  const nodeName =
    parentName !== undefined && index !== undefined
      ? `${parentName}.${index}`
      : 'visibility_condition';
  const nodeChildrenName = `${nodeName}.nodes` as const;

  const { fields, append, remove } = useFieldArray({
    control,
    name: nodeChildrenName,
  });

  const node: ElevionLogicalNode = useWatch({
    control,
    name: nodeName,
  });

  const addNewBlock = useCallback(() => {
    append(emptyLogicalNode, { shouldFocus: false });
  }, [append]);

  const addNewRule = useCallback(() => {
    append(emptyLeafNode, { shouldFocus: false });
  }, [append]);

  /** Handles the event for when an option is removed. */
  const removeRule = useCallback(
    (index) => {
      remove(index);
    },
    [remove],
  );

  return (
    <FlexBox
      flexDirection="column"
      padding="200"
      width="100%"
      className={classnames({ [styles.logicalBlock]: parentName })}
    >
      {fields.map((field, childIndex) => {
        const isNotLast = childIndex < fields.length - 1;
        const isLast = childIndex === fields.length - 1;
        const isNotLogicalNotOp = node.logicalOp !== 'NOT';
        const isFirst = childIndex === 0;
        return (
          <Box key={field.id}>
            <FlexBox>
              <FlexBox flex="1 1 auto">
                <ElevionCondition
                  index={childIndex}
                  parentName={nodeChildrenName}
                />
              </FlexBox>
              <FlexBox flex="0 0 auto" alignItems="flex-start" padding="0 200">
                <Box>
                  <IconButton
                    disabled={isFirst && isLast}
                    icon="trash"
                    variant="secondary"
                    onClick={() => removeRule(childIndex)}
                  >
                    Remove
                  </IconButton>
                </Box>
              </FlexBox>
            </FlexBox>

            {isNotLast && (
              <ElevionLogicalOp
                activeOp={node.logicalOp}
                readOnly={childIndex !== 0}
                parentName={nodeName}
              />
            )}

            {isLast && isNotLogicalNotOp && (
              <FlexBox margin="200 0 0 200">
                <Box margin="0 200 0 0">
                  <Button variant="secondary" onClick={addNewRule}>
                    Add rule
                  </Button>
                </Box>
                <Box>
                  <Button variant="secondary" onClick={addNewBlock}>
                    Add block
                  </Button>
                </Box>
              </FlexBox>
            )}
          </Box>
        );
      })}
    </FlexBox>
  );
});
